import React, { useState } from "react";
import { X, Phone, Hash, Pencil, Mail, CalendarDays, Users2, Radio, Tag as TagIcon } from "lucide-react";
import { format, parseISO } from "date-fns";

import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";

import ContactModal from "../ContactModal";
import NumeroComBandeira from "../NumeroComBandeira";
import {
	ehTelefone,
	rotuloDoIdentificador,
} from "../../helpers/identificadorDoContato";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import ParticipantesDoGrupo from "../ParticipantesDoGrupo";
import AtendimentosAnteriores from "../AtendimentosAnteriores";

const InfoRow = ({ icon: Icon, label, children }) => (
	<div className="flex items-start gap-2.5 text-sm">
		<Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
		<div className="min-w-0">
			<p className="text-xs text-muted-foreground">{label}</p>
			<div className="truncate font-medium">{children}</div>
		</div>
	</div>
);

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const [modalOpen, setModalOpen] = useState(false);

	const initials = (contact?.name || "?")
		.split(" ")
		.slice(0, 2)
		.map((n) => n[0])
		.join("")
		.toUpperCase();

	return (
		// No desktop o painel empurra a conversa para o lado. No celular não há
		// lado: 320px ao lado de uma tela de 390 espremiam a conversa a nada e
		// vazavam para fora. Ali ele vira uma camada por cima, ocupando a tela
		// inteira — e o X fecha e devolve a conversa.
		<div
			className={cn(
				"h-full overflow-hidden transition-[width] duration-200 ease-in-out",
				"max-sm:absolute max-sm:inset-0 max-sm:z-30 max-sm:transition-none",
				open
					? "w-80 shrink-0 max-sm:w-full"
					: "w-0 shrink-0 max-sm:pointer-events-none max-sm:hidden"
			)}
		>
		<div className="flex h-full w-80 flex-col border-l bg-background max-sm:w-full">
			<div className="flex min-h-[57px] shrink-0 items-center gap-2 border-b bg-muted/40 px-2">
				<Button variant="ghost" size="icon" onClick={handleDrawerClose}>
					<X className="h-5 w-5" />
				</Button>
				<span className="text-sm font-medium">{i18n.t("contactDrawer.header")}</span>
			</div>

			{loading ? (
				<ContactDrawerSkeleton />
			) : (
				<div className="flex-1 space-y-3 overflow-y-auto bg-muted/30 p-4">
					<ContactModal
						open={modalOpen}
						onClose={() => setModalOpen(false)}
						contactId={contact.id}
					/>

					{/* Profile card */}
					<div className="flex flex-col items-center gap-2 rounded-xl border bg-card p-6 text-center">
						<Avatar className="h-28 w-28">
							<AvatarImage src={contact.profilePicUrl} alt={contact.name} />
							<AvatarFallback className="text-2xl">{initials}</AvatarFallback>
						</Avatar>
						<p className="text-base font-semibold">{contact.name}</p>
						{contact.isGroup && (
							<Badge variant="secondary" className="gap-1">
								<Users2 className="h-3 w-3" />
								Grupo
							</Badge>
						)}
						<Button
							variant="outline"
							size="sm"
							className="mt-2"
							onClick={() => setModalOpen(true)}
						>
							<Pencil className="h-3.5 w-3.5" />
							{i18n.t("contactDrawer.buttons.edit")}
						</Button>
					</div>

					{/* Contact details */}
					<div className="space-y-3 rounded-xl border bg-card p-4">
						{contact.number &&
							(ehTelefone(contact.number, contact.isGroup, ticket?.whatsapp?.channel) ? (
								<InfoRow icon={Phone} label="Telefone">
									<a href={`tel:${contact.number}`} className="text-primary hover:underline">
										<NumeroComBandeira
											numero={contact.number}
											ehGrupo={contact.isGroup}
										/>
									</a>
								</InfoRow>
							) : (
								// Messenger, Direct, Telegram e chat do site: o campo guarda um
								// id, não um telefone. Mostrá-lo como telefone punha uma
								// bandeira de país inventada ao lado e um link que abria o
								// discador com um número inexistente.
								<InfoRow icon={Hash} label="Identificador">
									<span title={contact.number}>
										{rotuloDoIdentificador(contact.number, ticket?.whatsapp?.channel)}
									</span>
								</InfoRow>
							))}
						{contact.email && (
							<InfoRow icon={Mail} label="E-mail">
								<a href={`mailto:${contact.email}`} className="text-primary hover:underline">
									{contact.email}
								</a>
							</InfoRow>
						)}
						{contact.createdAt && (
							<InfoRow icon={CalendarDays} label="Cliente desde">
								{format(parseISO(contact.createdAt), "dd/MM/yyyy")}
							</InfoRow>
						)}
					</div>

					{/* Current ticket context */}
					{ticket?.id && (
						<div className="space-y-3 rounded-xl border bg-card p-4">
							<p className="text-sm font-semibold">Atendimento atual</p>
							{ticket.queue && (
								<InfoRow icon={TagIcon} label="Fila">
									<span className="inline-flex items-center gap-1.5">
										<span
											className="h-2 w-2 shrink-0 rounded-full"
											style={{ backgroundColor: ticket.queue.color }}
										/>
										{ticket.queue.name}
									</span>
								</InfoRow>
							)}
							{ticket.whatsapp && (
								<InfoRow icon={Radio} label="Conexão">
									{ticket.whatsapp.name}
								</InfoRow>
							)}
							{ticket.tags?.length > 0 && (
								<div className="flex flex-wrap gap-1.5 pt-1">
									{ticket.tags.map((tag) => (
										<span
											key={tag.id}
											className="rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
											style={{ backgroundColor: tag.color }}
										>
											{tag.name}
										</span>
									))}
								</div>
							)}
						</div>
					)}

					{/* O que já foi tratado com esta pessoa antes. Em grupo não vale:
					    ali o "contato" é o grupo inteiro e a lista viraria o próprio
					    histórico da sala. */}
					{!contact?.isGroup && contact?.id && (
						<AtendimentosAnteriores
							contactId={contact.id}
							ticketId={ticket?.id}
						/>
					)}

					{/* Grupo: quem está lá dentro. Em conversa individual a rota
					    devolve vazio e o painel nem aparece. */}
					{contact?.isGroup && ticket?.id && (
						<ParticipantesDoGrupo ticketId={ticket.id} />
					)}

					{contact?.extraInfo?.length > 0 && (
						<div className="rounded-xl border bg-card p-4">
							<p className="mb-2 text-sm font-semibold">
								{i18n.t("contactDrawer.extraInfo")}
							</p>
							<div className="space-y-2">
								{contact.extraInfo.map((info) => (
									<div key={info.id} className="rounded-lg border bg-muted/40 p-2.5">
										<p className="text-xs font-medium text-muted-foreground">
											{info.name}
										</p>
										<div className="truncate pt-0.5 text-sm">
											<MarkdownWrapper>{info.value}</MarkdownWrapper>
										</div>
									</div>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
		</div>
	);
};

export default ContactDrawer;
